import React, { Component } from "react";
import { Dropdown } from "semantic-ui-react";
import { AxiosResponse } from "axios";
import { getRequest } from "../../../utils/httpRequest";
import {
  Props,
  State,
  LeaderboardSetup,
  LeaderboardResource,
  Hero,
  LeaderboardYou,
} from "./Types";

const PAGE_SIZE = 7;

const getTotalPages = (data: LeaderboardResource) =>
  data.total_pages || data.total_heroes || 1;

const getTotalLearners = (data: LeaderboardResource) =>
  data.total_learners || data.heroes.length;

const initials = (name: string) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);

  if (parts.length > 1) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }

  const fallback = parts[0] || "";
  return `${fallback[0] || ""}${fallback[fallback.length - 1] || ""}`.toUpperCase();
};

const formatXp = (xp: number) => Number(xp || 0).toLocaleString();

const ordinal = (rank: number) => {
  const suffixes = ["th", "st", "nd", "rd"];
  const value = rank % 100;
  return `${rank}${suffixes[(value - 20) % 10] || suffixes[value] || suffixes[0]}`;
};

const FLAG_SYMBOLS = `
<symbol id="flag-gb" viewBox="0 0 512 512"><mask id="profile-lb-fm-gb-a"><circle cx="256" cy="256" r="256" fill="#fff"/></mask><g mask="url(#profile-lb-fm-gb-a)"><path fill="#eee" d="m0 0 8 22-8 23v23l32 54-32 54v32l32 48-32 48v32l32 54-32 54v68l22-8 23 8h23l54-32 54 32h32l48-32 48 32h32l54-32 54 32h68l-8-22 8-23v-23l-32-54 32-54v-32l-32-48 32-48v-32l-32-54 32-54V0l-22 8-23-8h-23l-54 32-54-32h-32l-48 32-48-32h-32l-54 32L68 0H0z"/><path fill="#0052b4" d="M336 0v108L444 0Zm176 68L404 176h108zM0 176h108L0 68ZM68 0l108 108V0Zm108 512V404L68 512ZM0 444l108-108H0Zm512-108H404l108 108Zm-68 176L336 404v108z"/><path fill="#d80027" d="M0 0v45l131 131h45L0 0zm208 0v208H0v96h208v208h96V304h208v-96H304V0h-96zm259 0L336 131v45L512 0h-45zM176 336 0 512h45l131-131v-45zm160 0 176 176v-45L381 336h-45z"/></g></symbol>
<symbol id="flag-us" viewBox="0 0 512 512"><mask id="profile-lb-fm-us-a"><circle cx="256" cy="256" r="256" fill="#fff"/></mask><g mask="url(#profile-lb-fm-us-a)"><path fill="#eee" d="M256 0h256v64l-32 32 32 32v64l-32 32 32 32v64l-32 32 32 32v64l-256 32L0 448v-64l32-32-32-32v-64z"/><path fill="#d80027" d="M224 64h288v64H224Zm0 128h288v64H256ZM0 320h512v64H0Zm0 128h512v64H0Z"/><path fill="#0052b4" d="M0 0h256v256H0Z"/><path fill="#eee" d="m187 243 57-41h-70l57 41-22-67zm-81 0 57-41H93l57 41-22-67zm-81 0 57-41H12l57 41-22-67zm162-81 57-41h-70l57 41-22-67zm-81 0 57-41H93l57 41-22-67zm-81 0 57-41H12l57 41-22-67Zm162-82 57-41h-70l57 41-22-67Zm-81 0 57-41H93l57 41-22-67zm-81 0 57-41H12l57 41-22-67Z"/></g></symbol>
<symbol id="flag-de" viewBox="0 0 512 512"><mask id="profile-lb-fm-de-a"><circle cx="256" cy="256" r="256" fill="#fff"/></mask><g mask="url(#profile-lb-fm-de-a)"><path fill="#ffda44" d="m0 345 256.7-25.5L512 345v167H0z"/><path fill="#d80027" d="m0 167 255-23 257 23v178H0z"/><path fill="#333" d="M0 0h512v167H0z"/></g></symbol>
<symbol id="flag-in" viewBox="0 0 512 512"><mask id="profile-lb-fm-in-a"><circle cx="256" cy="256" r="256" fill="#fff"/></mask><g mask="url(#profile-lb-fm-in-a)"><path fill="#eee" d="m0 160 256-32 256 32v192l-256 32L0 352z"/><path fill="#ff9811" d="M0 0h512v160H0Z"/><path fill="#6da544" d="M0 352h512v160H0Z"/><circle cx="256" cy="256" r="72" fill="#0052b4"/><circle cx="256" cy="256" r="48" fill="#eee"/><circle cx="256" cy="256" r="24" fill="#0052b4"/></g></symbol>
<symbol id="flag-kw" viewBox="0 0 512 512"><mask id="profile-lb-fm-kw-a"><circle cx="256" cy="256" r="256" fill="#fff"/></mask><g mask="url(#profile-lb-fm-kw-a)"><path fill="#eee" d="M138.4 147 512 167v178l-373.6 20z"/><path fill="#6da544" d="m0 0 138.4 167H512V0z"/><path fill="#d80027" d="m0 512 138.4-167H512v167z"/><path fill="#333" d="M167 167 0 0v512l167-167z"/></g></symbol>
<symbol id="flag-au" viewBox="0 0 512 512"><mask id="profile-lb-fm-au-a"><circle cx="256" cy="256" r="256" fill="#fff"/></mask><g mask="url(#profile-lb-fm-au-a)"><path fill="#0052b4" d="M0 0h512v512H0z"/><path fill="#eee" d="m154 300 14 30 32-8-14 30 25 20-32 7 1 33-26-21-26 21 1-33-33-7 26-20-14-30 32 8zm222-27h47l-38 27 15-44 14 44zm7-162 7 15 16-4-7 15 12 10-15 3v17l-13-11-13 11v-17l-15-3 12-10-7-15 16 4zm57 67 7 15 16-4-7 15 12 10-15 3v16l-13-10-13 11v-17l-15-3 12-10-7-15 16 4zm-122 22 7 15 16-4-7 15 12 10-15 3v16l-13-10-13 11v-17l-15-3 12-10-7-15 16 4zm65 156 7 15 16-4-7 15 12 10-15 3v17l-13-11-13 11v-17l-15-3 12-10-7-15 16 4zM0 0v32l32 32L0 96v160h32l32-32 32 32h32v-83l83 83h45l-8-16 8-15v-14l-83-83h83V96l-32-32 32-32V0H96L64 32 32 0Z"/><path fill="#d80027" d="M32 0v32H0v64h32v160h64V96h160V32H96V0Zm96 128 128 128v-31l-97-97z"/></g></symbol>
<symbol id="flag-za" viewBox="0 0 512 512"><mask id="profile-lb-fm-za-a"><circle cx="256" cy="256" r="256" fill="#fff"/></mask><g mask="url(#profile-lb-fm-za-a)"><path fill="#eee" d="m0 0 192 256L0 512h47l465-189v-34l-32-33 32-33v-34L47 0Z"/><path fill="#333" d="M0 142v228l140-114z"/><path fill="#ffda44" d="M192 256 0 95v47l114 114L0 370v47z"/><path fill="#6da544" d="M512 223H223L0 0v94l161 162L0 418v94l223-223h289z"/><path fill="#d80027" d="M512 0H47l189 189h276z"/><path fill="#0052b4" d="M512 512H47l189-189h276z"/></g></symbol>
<symbol id="flag-br" viewBox="0 0 512 512"><mask id="profile-lb-fm-br-a"><circle cx="256" cy="256" r="256" fill="#fff"/></mask><g mask="url(#profile-lb-fm-br-a)"><path fill="#6da544" d="M0 0h512v512H0z"/><path fill="#ffda44" d="M256 100.2 467.5 256 256 411.8 44.5 256z"/><path fill="#eee" d="M174.2 221a87 87 0 0 0-7.2 36.3l162 49.8a88.5 88.5 0 0 0 14.4-34c-40.6-65.3-119.7-80.3-169.1-52z"/><path fill="#0052b4" d="M255.7 167a89 89 0 0 0-41.9 10.6 89 89 0 0 0-39.6 43.4 181.7 181.7 0 0 1 169.1 52.2 89 89 0 0 0-9-59.4 89 89 0 0 0-78.6-46.8zM212 250.5a149 149 0 0 0-45 6.8 89 89 0 0 0 10.5 40.9 89 89 0 0 0 120.6 36.2 89 89 0 0 0 30.7-27.3A151 151 0 0 0 212 250.5z"/></g></symbol>
<symbol id="flag-ie" viewBox="0 0 512 512"><mask id="profile-lb-fm-ie-a"><circle cx="256" cy="256" r="256" fill="#fff"/></mask><g mask="url(#profile-lb-fm-ie-a)"><path fill="#eee" d="M167 0h178l25.9 252.3L345 512H167l-29.8-253.4z"/><path fill="#6da544" d="M0 0h167v512H0z"/><path fill="#ff9811" d="M345 0h167v512H345z"/></g></symbol>
<symbol id="flag-jp" viewBox="0 0 512 512"><mask id="profile-lb-fm-jp-a"><circle cx="256" cy="256" r="256" fill="#fff"/></mask><g mask="url(#profile-lb-fm-jp-a)"><path fill="#eee" d="M0 0h512v512H0z"/><circle cx="256" cy="256" r="111.3" fill="#d80027"/></g></symbol>
<symbol id="flag-ng" viewBox="0 0 512 512"><mask id="profile-lb-fm-ng-a"><circle cx="256" cy="256" r="256" fill="#fff"/></mask><g mask="url(#profile-lb-fm-ng-a)"><path fill="#6da544" d="M0 0v512h160l96-64 96 64h160V0H352l-96 64-96-64Z"/><path fill="#eee" d="M160 0h192v512H160Z"/></g></symbol>
<symbol id="flag-fr" viewBox="0 0 512 512"><mask id="profile-lb-fm-fr-a"><circle cx="256" cy="256" r="256" fill="#fff"/></mask><g mask="url(#profile-lb-fm-fr-a)"><path fill="#eee" d="M167 0h178l25.9 252.3L345 512H167l-29.8-253.4z"/><path fill="#0052b4" d="M0 0h167v512H0z"/><path fill="#d80027" d="M345 0h167v512H345z"/></g></symbol>
<symbol id="flag-nl" viewBox="0 0 512 512"><mask id="profile-lb-fm-nl-a"><circle cx="256" cy="256" r="256" fill="#fff"/></mask><g mask="url(#profile-lb-fm-nl-a)"><path fill="#eee" d="m0 167 253.8-19.3L512 167v178l-254.9 32.3L0 345z"/><path fill="#a2001d" d="M0 0h512v167H0z"/><path fill="#0052b4" d="M0 345h512v167H0z"/></g></symbol>
<symbol id="flag-ca" viewBox="0 0 512 512"><mask id="profile-lb-fm-ca-a"><circle cx="256" cy="256" r="256" fill="#fff"/></mask><g mask="url(#profile-lb-fm-ca-a)"><path fill="#d80027" d="M0 0v512h144l112-64 112 64h144V0H368L256 64 144 0Z"/><path fill="#eee" d="M144 0h224v512H144Z"/><path fill="#d80027" d="m301 289 44-22-22-11v-22l-45 22 23-44h-23l-22-34-22 33h-23l23 45-45-22v22l-22 11 45 22-12 23h45v33h22v-33h45z"/></g></symbol>
<symbol id="flag-pt" viewBox="0 0 512 512"><mask id="profile-lb-fm-pt-a"><circle cx="256" cy="256" r="256" fill="#fff"/></mask><g mask="url(#profile-lb-fm-pt-a)"><path fill="#6da544" d="M0 512h167l37.9-260.3L167 0H0z"/><path fill="#d80027" d="M512 0H167v512h345z"/><circle cx="167" cy="256" r="89" fill="#ffda44"/><path fill="#d80027" d="M116.9 211.5V267a50 50 0 1 0 100.1 0v-55.6H117z"/><path fill="#eee" d="M167 283.8c-9.2 0-16.7-7.5-16.7-16.7V245h33.4v22c0 9.2-7.5 16.7-16.7 16.7z"/></g></symbol>
`;

const COUNTRY_NAMES = {
  au: "Australia",
  br: "Brazil",
  ca: "Canada",
  de: "Germany",
  fr: "France",
  gb: "United Kingdom",
  ie: "Ireland",
  in: "India",
  jp: "Japan",
  kw: "Kuwait",
  ng: "Nigeria",
  nl: "Netherlands",
  pt: "Portugal",
  us: "United States",
  za: "South Africa",
};

const STATIC_FLAG_CODES = Object.keys(COUNTRY_NAMES);

const normalizeFlagCode = (countryCode: string) => {
  const code = (countryCode || "gb").toLowerCase();
  if (code === "uk") return "gb";
  return code;
};

const countryFlagImage = (countryCode: string) => {
  let img;

  try {
    img = require(`../../../../../assets/images/flags/svg/${countryCode}.svg`);
  } catch {
    img = "";
  }

  return img;
};

const uniqueFlagCodes = (codes: string[]) =>
  codes
    .map(normalizeFlagCode)
    .filter(
      (code, index, list) =>
        code && code !== "all" && list.indexOf(code) === index,
    );

const DynamicFlagSymbol = ({ code }: { code: string }) => {
  const image = countryFlagImage(code);

  if (!image) return null;

  const clipId = `profile-lb-flag-clip-${code}`;

  return (
    <symbol id={`flag-${code}`} viewBox="0 0 512 512">
      <clipPath id={clipId}>
        <circle cx="256" cy="256" r="256" />
      </clipPath>
      <image
        href={image}
        width="512"
        height="512"
        clipPath={`url(#${clipId})`}
        preserveAspectRatio="xMidYMid slice"
      />
    </symbol>
  );
};

const FlagSymbols = ({ countryCodes }: { countryCodes: string[] }) => {
  const dynamicCodes = uniqueFlagCodes(countryCodes).filter(
    (code) => STATIC_FLAG_CODES.indexOf(code) < 0,
  );

  return (
    <svg
      width="0"
      height="0"
      className="lb-flag-symbols"
      aria-hidden="true"
      focusable="false"
    >
      <defs dangerouslySetInnerHTML={{ __html: FLAG_SYMBOLS }} />
      {dynamicCodes.map((code) => (
        <DynamicFlagSymbol code={code} key={code} />
      ))}
    </svg>
  );
};

const Avatar = ({ hero, className = "lb-avatar" }) => (
  <span className={className} aria-hidden="true">
    {hero.profile_picture_url ? (
      <img src={hero.profile_picture_url} alt="" loading="lazy" />
    ) : (
      initials(hero.protected_name)
    )}
  </span>
);

const Flag = ({ hero }) => {
  const code = normalizeFlagCode(hero.country);

  return flagSvg(code);
};

const flagSvg = (code: string, size?: number) => (
  <>
    <svg
      className="lb-flag"
      style={size ? { width: size, height: size } : undefined}
      aria-hidden="true"
      focusable="false"
    >
      <use href={`#flag-${code}`} />
    </svg>
    <span className="sr-only">{COUNTRY_NAMES[code] || code.toUpperCase()}</span>
  </>
);

const YouPill = () => <span className="lb-youpill">You</span>;

const PodiumHero = ({ hero, place }: { hero: Hero; place: number }) => (
  <li className={`pd-${place}${hero.is_you ? " is-you" : ""}`}>
    <div className="pd-avatar-wrap">
      {place === 1 && (
        <span className="pd-crown" aria-hidden="true">
          <svg>
            <use href="#ln-crown"></use>
          </svg>
        </span>
      )}
      <Avatar hero={hero} className="pd-avatar" />
      <span className="pd-chip" aria-hidden="true">
        {place}
      </span>
    </div>
    <div className="pd-name">
      <span>{hero.protected_name}</span>
      <Flag hero={hero} />
      {hero.is_you && <YouPill />}
    </div>
    <div className="pd-xp">
      <strong>{formatXp(hero.xp)}</strong> XP
    </div>
  </li>
);

const TableHero = ({ hero, rank }: { hero: Hero; rank: number }) => (
  <tr className={hero.is_you ? "is-you" : ""}>
    <td className="lb-rank">{rank}</td>
    <td>
      <span className="lb-learner">
        <Avatar hero={hero} />
        <span className="lb-nm">{hero.protected_name}</span>
        {hero.is_you && <YouPill />}
      </span>
    </td>
    <td className="lb-xp">
      <strong>{formatXp(hero.xp)}</strong> XP
    </td>
    <td className="lb-country">
      <Flag hero={hero} />
    </td>
  </tr>
);

const youNudge = (you: LeaderboardYou) => {
  if (you.rank === 1) return "The view from the top.";

  if (you.ahead_name && typeof you.ahead_gap === "number") {
    return `${formatXp(you.ahead_gap)} XP behind ${you.ahead_name}. Closer than it sounds.`;
  }

  return "Keep climbing. The next rung is closer than it looks.";
};

const PinnedYou = ({ you }: { you: LeaderboardYou }) => (
  <div className="lb-youbar">
    <Avatar hero={you} />
    <div className="lb-youbar-id">
      <div className="lb-youbar-main">
        <span className="lb-youbar-rank">{ordinal(you.rank)}</span>
        <span className="lb-youbar-nm">{you.protected_name}</span>
        <YouPill />
        <Flag hero={you} />
      </div>
      <div className="lb-youbar-nudge">{youNudge(you)}</div>
    </div>
    <span className="lb-youbar-xp">
      <strong>{formatXp(you.xp)}</strong> XP
    </span>
  </div>
);

class Leaderboard extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      courses: [],
      countries: [],
      teams: [],
      heroes: [],
      topThreeHeroes: [],
      you: null,
      totalLearners: 0,
      totalPages: 1,
      selectedCourse: "",
      selectedCountry: "",
      selectedTeam: "",
      hasCurrentMemberShip: false,
      page: 0,
      loading: false,
    };
  }

  componentDidMount() {
    this.setState({ loading: true });

    return getRequest("/api/profile/leaderboard_setup.json").then(
      (response: AxiosResponse<LeaderboardSetup>) => {
        const { data } = response;
        const courses = [
          { text: "All Courses", value: "all", key: "all" },
        ].concat(
          data.courses.map((course) => ({
            text: course.name,
            value: course.slug,
            key: course.slug,
          })),
        );

        const countries = [
          { text: "All Countries", value: "all", key: "all" },
        ].concat(
          data.countries.map((countryResource) => {
            return {
              text: countryResource.country,
              value: countryResource.code,
              key: countryResource.code,
            };
          }),
        );

        const teams = [{ text: "All teams", value: "all", key: "all" }].concat(
          data.teams.map((team) => ({
            text: team.name,
            value: team.slug,
            key: team.slug,
          })),
        );

        this.setState(
          {
            courses,
            countries,
            teams,
            selectedTeam: data.team?.slug,
            hasCurrentMemberShip: data.has_current_membership,
          },
          () => {
            const url = data.team?.slug
              ? `/api/leaderboards/profile_search.json?organization_slug=${data.team.slug}`
              : "/api/leaderboards/profile_search.json";

            return getRequest(url).then(
              (response: AxiosResponse<LeaderboardResource>) => {
                const { data } = response;
                this.setState({
                  heroes: data.heroes.slice(3, 10),
                  topThreeHeroes: data.heroes.slice(0, 3),
                  you: data.you || null,
                  totalLearners: getTotalLearners(data),
                  totalPages: getTotalPages(data),
                  loading: false,
                });
              },
            );
          },
        );
      },
    );
  }

  handleChange(key, event, data) {
    this.setState({ [key]: data.value }, () => this.getHeroes());
  }

  handleKeyDown = (e) => {
    if (e.key === "Enter") {
      this.getHeroes();
    }
  };

  getHeroes = () => {
    const { selectedTeam, selectedCourse, selectedCountry } = this.state;

    return getRequest(
      `/api/leaderboards/profile_search.json?course_slug=${
        selectedCourse === "all" ? "" : selectedCourse
      }&organization_slug=${selectedTeam}&country=${
        selectedCountry === "all" ? "" : selectedCountry
      }`,
    ).then((response: AxiosResponse<LeaderboardResource>) => {
      const { data } = response;
      this.setState({
        topThreeHeroes: data.heroes.slice(0, 3),
        heroes: data.heroes.slice(3, 10),
        you: data.you || null,
        totalLearners: getTotalLearners(data),
        totalPages: getTotalPages(data),
        page: 0,
        loading: false,
      });
    });
  };

  getHeroesPagination = (newPage: number) => {
    const { selectedTeam, selectedCourse, selectedCountry } = this.state;

    return getRequest(
      `/api/leaderboards/profile_search.json?course_slug=${
        selectedCourse === "all" ? "" : selectedCourse
      }&organization_slug=${selectedTeam}&country=${
        selectedCountry === "all" ? "" : selectedCountry
      }&page=${newPage + 1}`,
    ).then((response: AxiosResponse<LeaderboardResource>) => {
      const { data } = response;
      this.setState({
        heroes: data.heroes,
        you: data.you || null,
        totalLearners: getTotalLearners(data),
        totalPages: getTotalPages(data),
        page: newPage,
        loading: false,
      });
    });
  };

  resetFilters = () => {
    this.setState(
      { selectedCourse: "", selectedCountry: "", selectedTeam: "" },
      () => this.getHeroes(),
    );
  };

  loadPrev = (event) => {
    event.preventDefault();
    const { page } = this.state;
    const newPage = this.prevPage(page);

    this.getHeroesPagination(newPage);
  };

  loadNext = (event) => {
    event.preventDefault();
    const { page, totalPages } = this.state;
    const newPage = this.nextPage(page, totalPages);

    this.getHeroesPagination(newPage);
  };

  prevPage = (page) => {
    if (page < 1) {
      return 0;
    } else if (page + 1 === 1) {
      return 1;
    } else {
      return page - 1;
    }
  };

  nextPage = (page, totalPages) =>
    page + 1 >= totalPages ? totalPages - 1 : page + 1;

  render() {
    const {
      topThreeHeroes,
      heroes,
      you,
      courses,
      countries,
      teams,
      selectedCourse,
      selectedCountry,
      selectedTeam,
      hasCurrentMemberShip,
      totalLearners,
      totalPages,
      page,
      loading,
    } = this.state;

    const currentCourse = courses.filter(
      (courses) => courses.value === selectedCourse,
    )[0];

    const currentCountry = countries.filter(
      (country) => country.value === selectedCountry,
    )[0];

    const currentTeam = teams.filter((team) => team.value === selectedTeam)[0];

    const currentTeamName = currentTeam
      ? currentTeam.text.length > 15
        ? currentTeam.text.slice(0, 10) + "..."
        : currentTeam.text
      : "";
    const scope = [
      currentCourse ? currentCourse.text : "All courses",
      currentCountry ? currentCountry.text : "All countries",
      currentTeam ? currentTeam.text : "All teams",
    ];
    const canLoadPrevious = page > 0;
    const canLoadNext = page + 1 < totalPages;
    const visiblePage = page + 1;
    const isYouVisible =
      !!you && topThreeHeroes.concat(heroes).some((hero) => hero.is_you);
    const showPinnedYou = !!you && !isYouVisible;
    const flagCodes = uniqueFlagCodes([
      ...countries.map((country) => country.value),
      ...topThreeHeroes.map((hero) => hero.country),
      ...heroes.map((hero) => hero.country),
      ...(you ? [you.country] : []),
    ]);

    return (
      <div
        className="leaderboard profile-landing-leaderboard"
        id="profile-leaderboard"
      >
        {loading && (
          <div className="ui active inverted dimmer">
            <div className="ui text loader">Loading</div>
          </div>
        )}
        <FlagSymbols countryCodes={flagCodes} />
        <div className="ui container">
          <div className="section-top">
            <div>
              <h1 className="section-heading">Leaderboard</h1>
              <p className="section-sub">The politest form of showing off.</p>
            </div>
          </div>

          <div className="lb-filters" aria-label="Leaderboard filters">
            <div className="lb-filter">
              <span className="lb-filter-label">Course</span>
              <Dropdown
                placeholder="All courses"
                fluid
                search
                scrolling
                selection
                value={selectedCourse}
                options={courses}
                onChange={this.handleChange.bind(this, "selectedCourse")}
              />
            </div>
            <div className="lb-filter">
              <span className="lb-filter-label">Country</span>
              <Dropdown
                placeholder="All countries"
                fluid
                search
                scrolling
                selection
                value={selectedCountry}
                options={countries}
                onChange={this.handleChange.bind(this, "selectedCountry")}
              />
            </div>
            {hasCurrentMemberShip && teams.length > 1 && (
              <div className="lb-filter">
                <span className="lb-filter-label">Team</span>
                {selectedTeam ? (
                  <Dropdown
                    fluid
                    search
                    selection
                    scrolling
                    labeled={true}
                    text={currentTeamName}
                    value={selectedTeam}
                    options={teams}
                    defaultValue={selectedTeam}
                    onChange={this.handleChange.bind(this, "selectedTeam")}
                  />
                ) : (
                  <Dropdown
                    placeholder="All teams"
                    fluid
                    scrolling
                    search
                    selection
                    text={currentTeamName}
                    value={selectedTeam}
                    options={teams}
                    onChange={this.handleChange.bind(this, "selectedTeam")}
                  />
                )}
              </div>
            )}
          </div>

          <div className="lb-board" aria-busy={loading}>
            <p className="lb-scope" aria-live="polite">
              {scope.map((label, index) => (
                <React.Fragment key={label}>
                  {index > 0 && (
                    <span className="sep" aria-hidden="true">
                      /
                    </span>
                  )}
                  <span>{label}</span>
                </React.Fragment>
              ))}
            </p>
            {totalLearners > 0 && (
              <p className="lb-count">
                {totalLearners.toLocaleString()}{" "}
                {totalLearners === 1 ? "learner" : "learners"}
              </p>
            )}

            {topThreeHeroes.length > 0 && (
              <ol className="lb-podium" aria-label="Top three learners">
                {topThreeHeroes.map((hero, idx) => (
                  <PodiumHero
                    hero={hero}
                    place={idx + 1}
                    key={`${idx}-${hero.protected_name}`}
                  />
                ))}
              </ol>
            )}

            {heroes.length > 0 && (
              <div className="lb-tablewrap">
                <table className="lb-table">
                  <caption className="sr-only">
                    Leaderboard rankings from fourth place
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">
                        <span className="lb-th-rank">Rank</span>
                      </th>
                      <th scope="col">Learner</th>
                      <th scope="col" className="col-xp">
                        XP
                      </th>
                      <th scope="col" className="col-country">
                        <span className="lb-th-country">Country</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {heroes.map((hero, index) => (
                      <TableHero
                        hero={hero}
                        rank={index + 4 + page * PAGE_SIZE}
                        key={`${index}-${hero.protected_name}`}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!loading && topThreeHeroes.length === 0 && heroes.length === 0 && (
              <p className="lb-empty">
                Nobody on this board yet. First one up the ladder gets the view.
              </p>
            )}

            {showPinnedYou && you && <PinnedYou you={you} />}

            <div className="lb-pager">
              <button
                className="lb-page-btn"
                type="button"
                onClick={this.loadPrev}
                disabled={!canLoadPrevious}
                aria-label="Previous page"
              >
                <i className="icon angle left" aria-hidden="true"></i>
              </button>
              <span className="lb-page-note">
                Page {visiblePage} of {totalPages}
              </span>
              <button
                className="lb-page-btn"
                type="button"
                onClick={this.loadNext}
                disabled={!canLoadNext}
                aria-label="Next page"
              >
                <i className="icon angle right" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default Leaderboard;
